import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, MessageCircle } from 'lucide-react';
import { api, formatDate, formatEUR, mensajeError } from '../lib/api';
import { abrirPestanaYa, llevarPestanaA } from '../lib/citasApi';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

/**
 * «Paquete online» (2026-08-27): manda por WhatsApp el presupuesto +
 * pruebas preoperatorias del paciente, en español o inglés a elegir. El
 * servidor prepara el mensaje acordado + enlace público del PDF y aquí solo
 * se abre WhatsApp con todo escrito; la compañera pulsa enviar. Marca el
 * presupuesto como «Enviado» automáticamente.
 *
 * `alcance` (2026-09-15, punto 4): 'paquete' es lo de siempre;
 * 'presupuesto' manda SOLO ese presupuesto, sin las pruebas. El mismo menú de
 * idioma sirve para los dos: español e inglés en todas las opciones.
 *
 * children = el trigger visual (botón). onSent se llama tras preparar el
 * envío (para refrescar la etiqueta del presupuesto en la lista).
 *
 * ---------------------------------------------------------------------------
 * PROPONER Y CONFIRMAR (última vuelta, 2026-09-15)
 * ---------------------------------------------------------------------------
 * El paquete arrastra TODOS los presupuestos vivos del paciente, y ningún
 * presupuesto nace «borrador»: `create_budget` los escribe siempre
 * «pendiente», que es uno de los estados vivos. O sea que la salvaguarda «los
 * borradores no salen de la clínica» no guardaba nada: un presupuesto a medio
 * escribir —un tanteo con un importe simbólico— se le podía mandar a la paciente dentro del paquete sin que nadie lo hubiera
 * mirado.
 *
 * Así que antes de mandar el paquete se enseña QUÉ va dentro —número,
 * concepto, importe y fecha de cada hoja, y la suma— y hay que decir que sí.
 * Lo que no deba salir se desmarca; el presupuesto desde el que se ha pulsado
 * va siempre y no se puede quitar, porque es el que se ha elegido mandar.
 *
 * El envío individual («Presupuesto PRES-…») no abre nada: ahí ya se lee en
 * el propio botón cuál es el que sale.
 */

// Los estados que el servidor da por VIVOS al armar el paquete
// (`_PRESUPUESTOS_VIVOS`, backend/routers/documents_router.py). Se repiten
// aquí para poder enseñar de antemano lo mismo que va a meter el servidor.
const ESTADOS_VIVOS = ['pendiente', 'sent'];

/** Hoy en fecha LOCAL: con toISOString, entre las 00:00 y las 02:00 de Madrid
 *  el día que sale es el de ayer y un presupuesto válido salía caducado. */
function hoyLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Caducado = su validez ya pasó, lo diga o no la etiqueta de estado. El
 *  último día válido es el propio `valid_until`, igual que cuenta el backend. */
function caducado(b) {
  if (!b) return false;
  if (b.status === 'expirado') return true;
  const hasta = String(b.valid_until || '').slice(0, 10);
  return !!hasta && hasta < hoyLocal();
}

/** Lo que se lee de un presupuesto para reconocerlo de un vistazo: sus
 *  conceptos tal y como salen impresos. Si no tiene líneas, la cirugía. */
function conceptoDe(b) {
  const conceptos = (b.items || [])
    .map((it) => String(it?.concept || '').trim())
    .filter(Boolean);
  if (conceptos.length) return conceptos.join(' · ');
  return b.surgery_name || 'Sin cirugía';
}

/**
 * Los presupuestos que el servidor metería en el paquete: el pulsado y los
 * demás vivos y sin caducar, en orden de emisión (mismo criterio y mismo
 * orden que `_presupuestos_del_paquete`). Nunca vacío.
 */
function hojasDelPaquete(pulsado, todos) {
  const elegidos = (todos || []).filter(
    (x) => x.id === pulsado.id
      || (ESTADOS_VIVOS.includes(x.status || 'draft') && !caducado(x)),
  );
  if (!elegidos.some((x) => x.id === pulsado.id)) elegidos.push(pulsado);
  return elegidos.sort((a, b) => (
    `${a.number || ''}|${a.created_at || ''}`.localeCompare(`${b.number || ''}|${b.created_at || ''}`)
  ));
}

/** ¿Lo que dice el servidor que ha metido es exactamente lo marcado? */
function mismaLista(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  const ordena = (xs) => [...xs].map(String).sort().join('|');
  return ordena(a) === ordena(b);
}

export default function WhatsAppPaqueteButton({ budget, children, onSent, dataTestid, alcance = 'paquete' }) {
  const esPaquete = alcance !== 'presupuesto';
  const [enviando, setEnviando] = useState(false);
  // El diálogo de confirmación: null = cerrado; { lang } = abierto para ese idioma.
  const [confirmacion, setConfirmacion] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [hojas, setHojas] = useState([]);
  const [marcados, setMarcados] = useState([]);
  // Si este servidor sabe o no dejar hojas fuera del paquete. Se aprende de su
  // respuesta (ver `mandar`) para no volver a pedírselo en balde.
  const servidorFiltra = useRef(null);

  const cerrar = () => {
    setConfirmacion(null);
    setHojas([]);
    setMarcados([]);
    setError('');
  };

  /**
   * Abre el diálogo y pide al SERVIDOR los presupuestos del paciente. Se
   * preguntan ahora, no se cogen los que tuviera pintados la pantalla: lo que
   * se enseña tiene que ser lo que se va a mandar, y entre que se abrió la
   * ficha y ahora puede haberse creado otro presupuesto.
   */
  const proponerPaquete = async (lang) => {
    setConfirmacion({ lang });
    setHojas([]);
    setMarcados([]);
    setError('');
    // Sin paciente no se pide nada: GET /api/budgets sin `patient_id` devuelve
    // los de TODA la clínica y en el diálogo saldrían presupuestos de otras
    // personas. No debería pasar nunca; si pasa, se dice y no se manda.
    if (!budget?.patient_id) {
      setError('Este presupuesto no tiene paciente: no se puede comprobar qué entraría en el paquete.');
      return;
    }
    setCargando(true);
    try {
      const r = await api.get('/api/budgets', { params: { patient_id: budget.patient_id } });
      // Segundo cinturón: solo los de ESTE paciente, pase lo que pase arriba.
      const todos = (r.data || []).filter((x) => x && x.patient_id === budget.patient_id);
      const lista = hojasDelPaquete(budget, todos);
      setHojas(lista);
      setMarcados(lista.map((x) => x.id));
    } catch (e) {
      setError(mensajeError(e, 'No se ha podido leer qué presupuestos entrarían en el paquete'));
    } finally {
      setCargando(false);
    }
  };

  const alternar = (id) => {
    // El pulsado va siempre: es el que alguien ha elegido mandar.
    if (id === budget.id) return;
    setMarcados((xs) => (xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]));
  };

  /** Nada ha salido de la clínica: se dice claro y no se abre WhatsApp. */
  const abortar = (ventana, servidorViejo) => {
    ventana?.close();
    toast.error('No se ha abierto WhatsApp: a la paciente no le ha llegado nada', {
      description: servidorViejo
        ? 'Este servidor todavía no sabe dejar un presupuesto fuera del paquete, así que '
          + 'habría mandado también el que has desmarcado. Manda el que quieras suelto con '
          + 'el botón «Presupuesto PRES-…». Revisa además las etiquetas: pueden haber '
          + 'quedado marcados como «Enviado» sin haberse enviado.'
        : 'El servidor iba a meter en el paquete unos presupuestos distintos de los '
          + 'marcados. Vuelve a abrir el envío y míralo otra vez.',
    });
    // Aunque no se haya mandado, el servidor ya ha podido tocar los estados:
    // que la pantalla enseñe la verdad de ahora.
    onSent?.();
  };

  /**
   * Prepara el WhatsApp. `ids` = null es el envío individual de siempre (ese
   * presupuesto y nada más); una lista es el paquete ya confirmado.
   */
  const mandar = async (lang, ids) => {
    if (enviando) return;
    const recorta = Array.isArray(ids) && ids.length < hojas.length;
    // Un servidor que no sabe recortar mandaría el paquete entero: ni se le
    // pide, que cada intento deja presupuestos marcados como «Enviado».
    if (recorta && servidorFiltra.current === false) {
      abortar(null, true);
      return;
    }
    setEnviando(true);
    // La pestaña se abre en el gesto del usuario y se redirige después:
    // Safari bloquea window.open si el POST tarda más de 1 s (revisión 2026-09-02).
    const ventana = abrirPestanaYa();
    // El individual va con la petición de siempre. El paquete lleva además la
    // lista que se acaba de confirmar: es la única forma de que salga lo que
    // se ha leído en el diálogo y no lo que el servidor decida por su cuenta
    // entre medias.
    const extra = ids === null
      ? '&alcance=presupuesto'
      : `&presupuestos=${ids.map((x) => encodeURIComponent(x)).join(',')}`;
    try {
      const r = await api.post(`/api/budgets/${budget.id}/enviar-whatsapp?lang=${lang}${extra}`);
      if (ids !== null) {
        // El servidor devuelve qué ha metido de verdad; si no lo devuelve es
        // que todavía no entiende la lista y ha armado el paquete a su modo.
        const confirmados = r.data?.presupuestos_enviados;
        if (Array.isArray(confirmados)) {
          servidorFiltra.current = true;
          if (!mismaLista(confirmados, ids)) { abortar(ventana, false); return; }
        } else {
          servidorFiltra.current = false;
          if (recorta) { abortar(ventana, true); return; }
        }
      }
      if (r.data?.whatsapp_url) {
        llevarPestanaA(ventana, r.data.whatsapp_url);
        onSent?.();
        cerrar();
      } else {
        ventana?.close();
      }
    } catch (e) {
      ventana?.close();
      toast.error(mensajeError(e, 'No se pudo preparar el WhatsApp'));
    } finally {
      setEnviando(false);
    }
  };

  // Elegir idioma: el paquete pasa por el diálogo; el individual sale directo,
  // que en su botón ya se lee qué presupuesto es.
  const elegirIdioma = (lang) => (esPaquete ? proponerPaquete(lang) : mandar(lang, null));

  const seleccionados = hojas.filter((x) => marcados.includes(x.id));
  // En el orden de las hojas, no en el de los clics: así la lista que se manda
  // es la misma que se está leyendo en pantalla.
  const idsMarcados = seleccionados.map((x) => x.id);
  const totalMarcado = seleccionados.reduce((s, x) => s + Number(x.total || 0), 0);
  const idioma = confirmacion?.lang === 'en' ? 'inglés' : 'español';

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild data-testid={dataTestid}>
          {children}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            Idioma del envío
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => elegirIdioma('es')}
            className="cursor-pointer"
            data-testid={dataTestid ? `${dataTestid}-es` : undefined}
          >
            <span className="mr-2 text-base leading-none">🇪🇸</span> Español
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => elegirIdioma('en')}
            className="cursor-pointer"
            data-testid={dataTestid ? `${dataTestid}-en` : undefined}
          >
            <span className="mr-2 text-base leading-none">🇬🇧</span> English
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={!!confirmacion} onOpenChange={(v) => { if (!v && !enviando) cerrar(); }}>
        <DialogContent className="max-w-lg" data-testid="paquete-confirmar">
          <DialogHeader>
            <DialogTitle>
              Qué le va a llegar a {budget?.patient_name || 'la paciente'}
            </DialogTitle>
            <DialogDescription>
              El paquete lleva estos presupuestos en {idioma} y, si el paciente las tiene,
              sus pruebas preoperatorias. Desmarca el que no deba salir; el presupuesto
              desde el que has pulsado va siempre.
            </DialogDescription>
          </DialogHeader>

          {cargando && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 mr-1.5 inline animate-spin" /> Mirando qué entraría…
            </div>
          )}

          {!cargando && error && (
            <p
              className="text-xs rounded bg-amber-50 text-amber-900 border border-amber-200 px-2 py-1.5"
              data-testid="paquete-confirmar-error"
            >
              {error} Mientras no se sepa qué va dentro, no se manda: cierra y vuelve a intentarlo.
            </p>
          )}

          {!cargando && !error && (
            <>
              <div className="divide-y divide-border max-h-72 overflow-y-auto">
                {hojas.map((b) => {
                  const pulsado = b.id === budget.id;
                  const marcado = marcados.includes(b.id);
                  return (
                    <div
                      key={b.id}
                      className="py-2.5 flex items-start gap-3"
                      data-testid={`paquete-confirmar-fila-${b.id}`}
                    >
                      <Checkbox
                        className="mt-0.5"
                        checked={marcado}
                        onCheckedChange={() => alternar(b.id)}
                        disabled={pulsado || enviando}
                        aria-label={`Incluir el presupuesto ${b.number}`}
                        data-testid={`paquete-confirmar-check-${b.id}`}
                      />
                      <div className="min-w-0 flex-1">
                        {/* Sin recortar el texto: si el concepto dice que es un
                            tanteo a medio escribir, tiene que leerse entero. */}
                        <div className={`text-sm font-medium break-words ${marcado ? '' : 'opacity-50 line-through'}`}>
                          <span className="text-[#0e6f64]">Presupuesto {b.number}</span> · {conceptoDe(b)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatDate(b.date)}
                          {pulsado ? ' · el que has pulsado' : ''}
                        </div>
                      </div>
                      <div className={`text-sm font-semibold tabular-nums shrink-0 ${marcado ? '' : 'opacity-50 line-through'}`}>
                        {formatEUR(b.total)}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div
                className="flex items-center justify-between gap-3 border-t border-border pt-2 text-sm"
                data-testid="paquete-confirmar-total"
              >
                <span className="text-muted-foreground">
                  {seleccionados.length === 1 ? '1 presupuesto' : `${seleccionados.length} presupuestos`}
                  {' · en '}{idioma}
                </span>
                <span className="font-semibold tabular-nums">{formatEUR(totalMarcado)}</span>
              </div>
            </>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={cerrar}
              disabled={enviando}
              data-testid="paquete-confirmar-cancelar"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => mandar(confirmacion?.lang, idsMarcados)}
              disabled={enviando || cargando || !!error || !idsMarcados.length}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
              data-testid="paquete-confirmar-enviar"
            >
              {enviando
                ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                : <MessageCircle className="w-4 h-4 mr-1.5" />}
              Mandar por WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
